<template>
  <div class="wheel">
    <!-- Wheel canvas section-->
    <div ref="wrapper" class="wheel__wrapper" :style="wrapperStyle">
      <div v-if="!hideControls" class="wheel__prev" @click="changeMonth(-1)">
        <img class="f-w f-h" src="/images/prev.png" alt="">
      </div>
      <div class="wheel__canvas-container">
        <canvas ref="wheelCanvas" />
      </div>
      <div v-if="!hideControls" class="wheel__next" @click="changeMonth(1)">
        <img class="f-w f-h" src="/images/next.png" alt="">
      </div>
    </div>
    <!-- END Wheel canvas section-->
    <!-- Wheel information section -->
    <div v-if="!hideInfo">
      <div
        v-for="indic in indicators"
        :key="indic.id"
        class="wheel__indicator"
      >
        <div class="wheel__indicator__label">{{ indic.legend }}</div>
        <div class="wheel__indicator__name">
          <RouterLink :to="createIndicatorLink(indic)">
            {{ decodeHTML(indic.name) }}
          </RouterLink>
        </div>
        <div class="wheel__indicator__today v-centerer">
          <div class="v-centerer wheel__indicator__today__wrapper">
            <span class="wheel__indicator__today__legend">
              {{ $t('message.TODAY') + $t('message.TWO_DOTS') }}
            </span>
            &nbsp;
            <span class="wheel__indicator__today__value" :style="todayValueRowStyles(indic)">
              {{ numberToString(indic.todayValue) + (indic.unit ? ` ${indic.unit}` : '') }}
            </span>
          </div>
          <div class="v-centerer wheel__indicator__today__objective">
            <span>
              {{ indic.isObjectiveMax ? $t('message.MAXIMUM_SHORT') : $t('message.MINIMUM_SHORT') }}
            </span>
            &nbsp;
            <span>
              {{ $t('message.TWO_DOTS') }}
              {{ numberToString(indic.todayObjective || indic.objective) }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- END Wheel information section -->
  </div>
</template>

<script>
/**
 * Display all indicators values of the current months in a given theme
 *
 * @vue-prop {Object} theme - Theme object containing indicators and its values
 * @vue-prop {moment} value - Month range ; values of the chosen month will be displayed
 * @vue-prop {boolean} hideControls - Hide the navigation arrows
 * @vue-prop {boolean} hideInfo - Hide additional info
 * @vue-prop {boolean} isResizing - Is parent resizing? Prevent issue due to chart multiple resize
 *
 * @vue-event {moment} input - Emit new range, triggered on arrow click
 */

import $ from 'jquery';
import Moment from 'moment-timezone';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import mixinCommonColors from '@/mixins/common-colors.vue';
// Utils
import { decodeHTML } from '@/utils/main';
import { valueKeyFormat } from '@/utils/indicator-utils';
import { SColors } from '@/utils/constants';

/**
 * Test if indicator value is overrunning objective
 * @param {number} value - Indicator value
 * @param {number} objective - Indicator objective
 * @param {number} isObjectiveMax - Is the indicator objective is a maximum
 *                                  False means its a minimum
 */
function isOverrunning(value, objective, isObjectiveMax) {
  return isObjectiveMax
    ? value > objective
    : value < objective;
}

const WHEEL_MAX_HEIGHT = 300;

export default {

  name: 'Wheel',

  mixins: [mixinCommonColors],

  props: {
    theme: {
      type: Object,
      required: true,
    },
    value: {
      type: Object, // moment object
      validator(value) {
        return (Moment.isMoment(value)) && value.isValid();
      },
      default() {
        return Moment();
      },
    },
    hideControls: Boolean,
    hideInfo: Boolean,
    isResizing: Boolean,
  },

  data() {
    return {
      wheelChart: null,
      date: this.value.clone().startOf('month'),
      datasetsLabels: 'ABCDEFGHI',
      wrapperHeight: WHEEL_MAX_HEIGHT,
    };
  },

  computed: {
    /** Object: CSS style for the canvas wraper */
    wrapperStyle() {
      return { maxHeight: `${this.wrapperHeight}px` };
    },
    /**
     * Reformat indicators in theme to be usable by the component
     * @return {Object[]} Formatted indicator list
     */
    indicators() {
      const now = this.$moment();
      const dateKey = now.format(valueKeyFormat);
      return this.theme.indicators.map((indic, index) => {
        let todayVal = indic.todayValue !== null && indic.todayValue !== undefined
          ? Number(indic.todayValue)
          : NaN;
        if (isNaN(todayVal) && indic.inputValues) {
          const todayData = indic.inputValues[dateKey];
          if (todayData) {
            todayVal = todayData.value === null ? NaN : todayData.value;
          }
        }
        let todayObj = indic.todayObjective !== undefined ? Number(indic.todayObjective) : NaN;
        if (isNaN(todayObj)) {
          const tObj = indic.objectives[dateKey];
          if (tObj !== undefined) {
            todayObj = tObj;
          }
        }
        return {
          ...this.$store.state.indicators[`${indic.id}`],
          id: Number(indic.id),
          name: indic.name,
          legend: this.datasetsLabels[Math.min(index, this.datasetsLabels.length - 1)],
          isObjectiveMax: indic.objectiveIsMax === 1,
          todayValue: todayVal,
          todayObjective: todayObj,
        };
      });
    },
    /**
     * Config object of the wheel, will be put in Chart constructor
     * @return {Object} Chart config object
     */
    chartConfig() {
      const datasets = this.indicators.map(indic => this.createDataset(indic, this.date));
      return {
        plugins: [ChartDataLabels],
        type: 'doughnut',
        data: { datasets },
        options: {
          rotation: -180 / (datasets[0].data.length + 1),
          cutout: '50%',
          responsive: !this.isResizing,
          aspectRatio: 1,
          maintainAspectRatio: false,
          elements: {
            center: {
              text: this.theme.letter,
              color: SColors.mainTextColor,
              fontStyle: 'Work Sans',
              maxFontSize: 25,
            },
          },
          plugins: {
            datalabels: {
              display: ctx => ctx.dataIndex === 0 || (ctx.datasetIndex === 0 && ctx.dataIndex % 4 === 0),
              formatter: (val, ctx) => ctx.dataset.labels[ctx.dataIndex],
              color: SColors.mainTextColor,
              font: ctx => ({ weight: '600', size: ctx.dataIndex === 0 ? 11 : 14 }),
              align: ctx => (ctx.dataIndex === 0 ? 'center' : 'end'),
              anchor: ctx => (ctx.dataIndex === 0 ? 'center' : 'end'),
              offset: ctx => (ctx.dataIndex === 0 ? 0 : 4),
            },
            tooltip: {
              callbacks: {
                // Tooltip header: Display date and value
                title: (tooltipItems) => {
                  const { dataIndex, dataset } = tooltipItems[0];
                  if (dataIndex === 0) {
                    return dataset.indicName;
                  }
                  const dateStr = dataset.dates[dataIndex].format(this.$t('minDateFormat'));
                  const value = dataset.values[dataIndex];
                  const unit = dataset.unit ? ` ${dataset.unit}` : '';
                  return !Number.isNaN(value) && value !== null
                    ? `${dateStr}${this.$t('message.TWO_DOTS')}${value}${unit}`
                    : `${dateStr}`;
                },
                afterBody: (item) => {
                  const { dataIndex, dataset } = item[0];
                  return { comment: dataset.comments[dataIndex] };
                },
              },
            },
          },
        },
      };
    },
  },

  watch: {
    // When value (month range) props change, update internal range value
    value(newValue) {
      this.date = newValue.clone().startOf('month');
    },
    // Will redraw chart after any config change
    chartConfig() {
      this.drawChart();
    },
    // Container will be resized ; Canvas need to be redrawn (doesn't trigger chart.resize)
    hideControls() {
      this.$nextTick(this.drawChart);
    },
    hideInfo() {
      this.skipResize();
    },
  },

  mounted() {
    this.drawChart();
    this.checkWrapperHeight();
    $(window).on(this.resizeEventName, this.checkWrapperHeight);
  },

  beforeDestroy() {
    if (this.wheelChart) {
      this.wheelChart.destroy();
    }
  },

  methods: {
    decodeHTML,
    /**
     * Cast Number to String ; Display '-' instead of 'NaN'
     * @param {number} value - Value that will be casted
     * @return {string} Casted value
     */
    numberToString(value) {
      return Number.isNaN(value) ? '-' : String(Number(value).sToFixed(2));
    },
    /** Determine the height of the canvas wrapper */
    checkWrapperHeight() {
      if (this.$refs.wrapper) {
        const { offsetWidth } = this.$refs.wrapper;
        this.wrapperHeight = offsetWidth > WHEEL_MAX_HEIGHT ? WHEEL_MAX_HEIGHT : offsetWidth;
      }
    },
    /**
     * Create link for indicator rows
     * @param {Object} indic - Indicator object
     * @return {Object} Link ; Used by RouterLink components
     */
    createIndicatorLink(indic) {
      const date = this.date.clone();
      return {
        path: `/indicator?detailedIndicatorId=${indic.id}`,
        query: {
          rangeStart: date.startOf('month').format('YYYY-MM-DD'),
          rangeEnd: date.endOf('month').format('YYYY-MM-DD'),
        },
      };
    },
    /**
     * Styles list for daily value rows
     * @param {Object} indic - Indicator object
     * @return {Object} Styles list
     */
    todayValueRowStyles(indic) {
      let color;
      if (!Number.isNaN(indic.todayValue)) {
        color = isOverrunning(indic.todayValue, indic.todayObjective, indic.isObjectiveMax)
          ? SColors.indicatorsRedLine
          : SColors.indicatorsGreenLine;
      }
      return { color };
    },
    // Component functions
    /**
     * Change current (internal) range value ; emit updated value
     * @param {number} monthChangeOffset - Month changing offset
     */
    changeMonth(monthChangeOffset) {
      this.$emit('input', this.date.clone().add(monthChangeOffset, 'month'));
    },
    /**
     * Create dataset of an indicator, used to create Chart datasets array
     * @param {Object} indic - Indicator object
     * @param {moment} date - Month range ; Only the month and year will be used
     * @param {string} dateFormat - Date format used for wheel tooltips
     */
    createDataset(indic, date) {
      // Add a legend as first tile of the doughtnut
      const colors = ['#fff'];
      const dataset = {
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        data: [1],
        labels: [indic.legend],
        values: [NaN],
        dates: [date.clone()],
        comments: [''],
        indicName: decodeHTML(indic.name),
        unit: indic.unit,
        borderWidth: 2,
      };
      for (
        const dateItr = date.clone().startOf('month'), dateMax = date.clone().endOf('month');
        dateItr < dateMax;
        dateItr.add(1, 'days')
      ) {
        const dateKey = dateItr.format(valueKeyFormat);
        const value = indic.inputValues && indic.inputValues[dateKey];
        const obj = indic.objectives ? indic.objectives[dateKey] : indic.objective;
        let commentText = '';
        let numValue = null;
        if (!value || value.value === null) {
          colors.push('#f1f1f1');
        } else {
          colors.push(
            isOverrunning(value.value, obj, indic.isObjectiveMax)
              ? SColors.indicatorsRedLine
              : SColors.indicatorsGreenLine,
          );
          // eslint-disable-next-line prefer-destructuring
          commentText = value.commentText;
          numValue = value.value;
        }
        dataset.data.push(1);
        dataset.labels.push(dateItr.format('D'));
        dataset.values.push(numValue);
        dataset.dates.push(dateItr.clone());
        dataset.comments.push(commentText);
      }
      return dataset;
    },
    /**
     * Draw/redraw wheel canvas
     */
    drawChart() {
      const canvas = this.$refs.wheelCanvas;
      if (!canvas) {
        return;
      }
      if (this.wheelChart) {
        this.wheelChart.destroy();
      }
      if (!this.isResizing) {
        const context = canvas.getContext('2d');
        this.wheelChart = new Chart(context, this.chartConfig);
      }
    },
    /**
     * Destroy and redraw chart to prevent a chart.resize
     * (chart.resize in extremly costly on this chart)
     */
    skipResize() {
      if (this.wheelChart) {
        this.wheelChart.destroy();
        this.$nextTick(this.drawChart);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/grid.scss";

$doughtnut-red-color: #f13122;
$legend-label-color: #787878;
$control-button-width: 30px;

/** Base */
.wheel {
  align-items: left;
  box-sizing: border-box;
  height: 100%;
  justify-content: center;
  padding: 16px;

  &__indicator {
    margin-top: 2rem;

    &__label {
      font-size: 1.3rem;
      font-weight: 600;
      margin-bottom: .2rem;
    }

    &__today {
      margin-top: .5rem;
      max-width: 250px;
      justify-content: space-between;
      flex-wrap: wrap;

      &__wrapper {
        white-space: nowrap;
      }

      &__legend {
        font-size: 0.85rem;
      }

      &__value {
        font-size: 1.1rem;
        font-weight: 600;
      }

      &__objective {
        background-color: $color-shizen-light-cyan;
        padding: 0.25rem 0.3rem;
        border-radius: 3px;
        white-space: nowrap;
      }
    }
  }
}

/** Canvas wrapper */
.wheel__wrapper {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  justify-content: center;
  height: 100%;

  &.wheel--column {
    // Set accordingly to .wheel__info
    height: 65%;
    max-height: 65%;
  }
}

.wheel__prev,
.wheel__next {
  align-items: center;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  height: 50px;
  justify-content: center;
  padding: 5px;
  transition: background 0.2s linear;
  width: $control-button-width;
  &:hover {
    background: #c0daf8;
  }
}

.wheel__canvas-container {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  width: calc(100% - #{$control-button-width * 2});
  position: relative;
  overflow: hidden;
}
</style>
